import { Capacitor, registerPlugin } from '@capacitor/core';
import { usesBearerAuth } from './apiBase.ts';
import { recordSwallowed } from './swallowed.ts';

interface PbforkAuthPlugin {
  getToken(): Promise<{ token: string | null }>;
  setToken(options: { token: string }): Promise<void>;
  clearToken(): Promise<void>;
}

const plugin = Capacitor.isNativePlatform() ? registerPlugin<PbforkAuthPlugin>('PbforkAuth') : null;
const WEB_SESSION_KEY = 'pbfork.authToken';
/** Jetonun son kullanma zamanı (epoch ms). Sır değildir; cihazda kalabilir. */
const EXPIRY_KEY = 'pbfork.authTokenExpiresAt';

let cached: string | null | undefined;
let expiresAt = 0;

function readExpiry(): number {
  if (typeof localStorage === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(EXPIRY_KEY);
    const value = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(value) ? value : 0;
  } catch (error) {
    recordSwallowed('authToken', error);
    return 0;
  }
}

function writeExpiry(value: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value > 0) localStorage.setItem(EXPIRY_KEY, String(value));
    else localStorage.removeItem(EXPIRY_KEY);
  } catch (error) {
    recordSwallowed('authToken', error);
    // Gizli mod: yalnız bellekte kalır.
  }
}

function expired(now = Date.now()): boolean {
  return expiresAt > 0 && now >= expiresAt;
}

/**
 * Bearer modunda tokenı döndürür. Native uygulamada Android Keystore,
 * tarayıcı geliştirmesinde yalnızca sessionStorage kullanılır.
 *
 * Süresi dolmuş jeton **döndürülmez**: istemci önce 401 alıp sonra giriş
 * ekranına düşmek yerine doğrudan oturumu kapalı görür.
 */
export async function getAuthToken(): Promise<string | null> {
  if (!usesBearerAuth) return null;

  if (cached !== undefined) {
    if (cached !== null && expired()) {
      await clearAuthToken();
      return null;
    }
    return cached;
  }

  if (plugin !== null) {
    try {
      cached = (await plugin.getToken()).token ?? null;
    } catch (error) {
      recordSwallowed('authToken', error);
      cached = null;
    }
    expiresAt = readExpiry();
    if (cached !== null && expired()) {
      await clearAuthToken();
      return null;
    }
    return cached;
  }

  try {
    cached = window.sessionStorage.getItem(WEB_SESSION_KEY);
  } catch (error) {
    recordSwallowed('authToken', error);
    cached = null;
  }
  expiresAt = readExpiry();
  if (cached !== null && expired()) {
    await clearAuthToken();
    return null;
  }
  return cached;
}

export async function setAuthToken(token: string, tokenExpiresAt?: number): Promise<void> {
  cached = token;
  expiresAt = tokenExpiresAt !== undefined && tokenExpiresAt > 0 ? tokenExpiresAt : 0;
  writeExpiry(expiresAt);
  if (!usesBearerAuth) return;
  if (plugin !== null) {
    await plugin.setToken({ token });
    return;
  }
  try {
    window.sessionStorage.setItem(WEB_SESSION_KEY, token);
  } catch (error) {
    recordSwallowed('authToken', error);
    // Gizli mod: yalnız bellekte kalır.
  }
}

export async function clearAuthToken(): Promise<void> {
  cached = null;
  expiresAt = 0;
  writeExpiry(0);
  if (plugin !== null) {
    await plugin.clearToken().catch(() => undefined);
    return;
  }
  try {
    window.sessionStorage.removeItem(WEB_SESSION_KEY);
  } catch (error) {
    recordSwallowed('authToken', error);
    // yok sayılır
  }
}
